/**
 * Shared API Types
 * 
 * Common TypeScript interfaces for API responses
 */

import { isRecord } from '@/utils/type-guards';

/**
 * Error response from the backend API
 */
export interface PreFlightBlockData {
  actionId?: string;
  featureId?: string;
  costMode?: string;
  estimatedCredits?: number | null;
  estimatedCostUsd?: number | null;
  estimatedChargedUsd?: number | null;
  creditUsdValue?: number | null;
  governanceCredits?: number | null;
  primaryDebitFromId?: string | null;
  isMixedDebit?: boolean;
  negativeToleranceCredits?: number | null;
  blockRef?: string;
}

export interface ApiErrorDetailData {
  preFlight?: PreFlightBlockData;
  auditResults?: unknown[];
  decisionActionId?: string;
  outcomeId?: string;
  reason?: string;
  resultIds?: string[];
  auditCostId?: string;
  sourceId?: string;
  stageId?: string;
  resourceKindId?: string;
  fileName?: string;
  [key: string]: unknown;
}

export interface ApiErrorDetail {
  message?: string;
  ref?: string;
  data?: ApiErrorDetailData;
}

export interface ApiErrorDetails {
  ref?: string;
  data?: ApiErrorDetailData;
  errors?: Array<{
    field: string;
    message: string;
    type: string;
  }>;
}

export interface ApiErrorResponse {
  success: false;
  error_code?: string;
  message: string;
  detail?: string | ApiErrorDetail;
  details?: ApiErrorDetails;
  timestamp?: string;
}

export interface AuditBlockData {
  auditResults: unknown[];
  decisionActionId?: string;
  outcomeId?: string;
  reason?: string;
  resultIds: string[];
  auditCostId?: string;
  sessionId?: string;
  sourceId?: string;
  stageId?: string;
  resourceKindId?: string;
  fileName?: string;
}

/**
 * Success response from the backend API
 */
export interface ApiSuccessResponse<T> {
  success: true;
  message: T;
  timestamp: string;
}

/**
 * Generic API response (can be success or error)
 */
export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

/**
 * Helper type guard to check if response is an error
 */
export function isApiError(response: unknown): response is ApiErrorResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'success' in response &&
    (response as { success: boolean }).success === false
  );
}

/**
 * Helper to extract error message from API error response
 */
export function extractErrorMessage(response: ApiErrorResponse, fallback: string): string {
  if (response.message) {
    return response.message;
  }
  return fallback;
}

const INSUFFICIENT_CREDIT_REFS = new Set([
  'all_budgets_exhausted',
  'api_key_budget_exhausted',
  'company_balance_missing',
  'company_pool_exhausted',
  'no_active_plan',
  'subscription_canceled_period_ended',
  'subscription_expired',
  'team_budget_exhausted',
  'team_overage_not_allowed',
  'trial_ended',
  'user_overage_cap_exhausted',
  'user_overage_not_allowed',
  'user_plan_balance_exhausted',
]);

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function readNullableNumber(value: unknown): number | null | undefined {
  if (value === null) {
    return null;
  }
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asPreFlightBlockData(value: unknown): PreFlightBlockData | null {
  if (!isRecord(value)) {
    return null;
  }

  const data: PreFlightBlockData = {
    actionId: readString(value.actionId),
    featureId: readString(value.featureId),
    costMode: readString(value.costMode),
    estimatedCredits: readNullableNumber(value.estimatedCredits),
    estimatedCostUsd: readNullableNumber(value.estimatedCostUsd),
    estimatedChargedUsd: readNullableNumber(value.estimatedChargedUsd),
    creditUsdValue: readNullableNumber(value.creditUsdValue),
    governanceCredits: readNullableNumber(value.governanceCredits),
    primaryDebitFromId:
      value.primaryDebitFromId === null ? null : readString(value.primaryDebitFromId),
    isMixedDebit: readBoolean(value.isMixedDebit),
    negativeToleranceCredits: readNullableNumber(value.negativeToleranceCredits),
    blockRef: readString(value.blockRef),
  };

  return Object.values(data).some((fieldValue) => fieldValue !== undefined) ? data : null;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

function extractErrorDetail(response: ApiErrorResponse): ApiErrorDetails | null {
  return response.details ?? null;
}

/**
 * Extract the stable backend error reference from the standard API envelope.
 */
export function extractErrorRef(response: ApiErrorResponse): string | undefined {
  return readString(extractErrorDetail(response)?.ref);
}

export function extractAuditBlock(response: ApiErrorResponse): AuditBlockData | null {
  const errorRef = extractErrorRef(response);
  if (
    errorRef !== 'flexible_audit'
    && errorRef !== 'flexible_audit_lock'
    && errorRef !== 'audit'
  ) {
    return null;
  }

  const detail = extractErrorDetail(response);
  const data = detail?.data;
  if (!isRecord(data)) {
    return null;
  }

  const auditResults = Array.isArray(data.auditResults) ? data.auditResults : [];
  return {
    auditResults,
    decisionActionId: readString(data.decisionActionId),
    outcomeId: readString(data.outcomeId),
    reason: readString(data.reason),
    resultIds: readStringArray(data.resultIds),
    auditCostId: readString(data.auditCostId),
    sessionId: readString(data.sessionId),
    sourceId: readString(data.sourceId),
    stageId: readString(data.stageId),
    resourceKindId: readString(data.resourceKindId),
    fileName: readString(data.fileName),
  };
}

export function isAuditBlock(response: ApiErrorResponse, statusCode?: number): boolean {
  if (typeof statusCode === 'number' && statusCode !== 403) {
    return false;
  }
  return extractAuditBlock(response) !== null;
}

/**
 * Extract safe preflight context from a standard runtime billing 402 response.
 */
export function extractPreFlightBlock(response: ApiErrorResponse): PreFlightBlockData | null {
  const detail = extractErrorDetail(response);
  const preFlight = detail?.data?.preFlight;
  return asPreFlightBlockData(preFlight);
}

export function isPreFlightBlock(
  response: ApiErrorResponse,
  statusCode?: number
): boolean {
  if (typeof statusCode === 'number' && statusCode !== 402) {
    return false;
  }
  return extractPreFlightBlock(response) !== null;
}

export function isInsufficientCreditBlock(
  response: ApiErrorResponse,
  statusCode?: number
): boolean {
  if (typeof statusCode === 'number' && statusCode !== 402) {
    return false;
  }
  if (isPreFlightBlock(response, statusCode)) {
    return true;
  }

  const ref = extractErrorRef(response);
  return Boolean(ref && INSUFFICIENT_CREDIT_REFS.has(ref));
}
